/**
 * Pengumuman (Announcement) — broadcast hub for HR/Director.
 * Staff sees same page in read-only mode.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Megaphone, Plus, Trash2, X, AlertCircle, Pin, PartyPopper, CalendarDays, Loader,
} from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = {
  general:     { label: 'Umum',       icon: Megaphone,    color: 'bg-blue-50 text-blue-700 border-blue-200' },
  urgent:      { label: 'Urgent',     icon: AlertCircle,  color: 'bg-red-50 text-red-700 border-red-200' },
  holiday:     { label: 'Cuti',       icon: CalendarDays, color: 'bg-amber-50 text-amber-700 border-amber-200' },
  celebration: { label: 'Celebration', icon: PartyPopper, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

const AnnouncementForm = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    title: '', body: '', category: 'general', pinned: false, expires_at: '',
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/announcements`, {
        ...form,
        expires_at: form.expires_at || null,
      });
      toast.success('Pengumuman dipublish!');
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal publish');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-3" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="bg-white rounded-2xl w-full max-w-lg p-5 sm:p-6 shadow-xl space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-800">Pengumuman Baru</h3>
          <button type="button" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <div>
          <label className="text-xs text-gray-500">Tajuk</label>
          <input value={form.title} onChange={(e) => setForm(s => ({ ...s, title: e.target.value }))}
            required maxLength={200} data-testid="ann-title"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
        </div>
        <div>
          <label className="text-xs text-gray-500">Mesej</label>
          <textarea value={form.body} onChange={(e) => setForm(s => ({ ...s, body: e.target.value }))}
            required rows={5} maxLength={5000} data-testid="ann-body"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-500">Kategori</label>
            <select value={form.category} onChange={(e) => setForm(s => ({ ...s, category: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
              <option value="general">Umum</option>
              <option value="urgent">Urgent</option>
              <option value="holiday">Cuti</option>
              <option value="celebration">Celebration</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500">Tarikh Tamat (opt)</label>
            <input type="date" value={form.expires_at}
              onChange={(e) => setForm(s => ({ ...s, expires_at: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={form.pinned}
            onChange={(e) => setForm(s => ({ ...s, pinned: e.target.checked }))} />
          <Pin className="w-4 h-4" /> Pin di atas (highlight)
        </label>
        <button type="submit" disabled={busy} data-testid="ann-submit"
          className="w-full bg-red-700 text-white py-2.5 rounded-lg font-medium hover:bg-red-800 disabled:opacity-50">
          {busy ? 'Memproses…' : 'Publish Pengumuman'}
        </button>
      </form>
    </div>
  );
};

const AnnouncementsPage = () => {
  const { userRole } = useApp();
  const isAdmin = ['director', 'finance', 'hr'].includes(userRole);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/announcements`);
      setItems(data.announcements || []);
    } catch (e) {
      toast.error('Gagal memuat pengumuman');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleDelete = async (id) => {
    if (!window.confirm('Padam pengumuman ni?')) return;
    try {
      await axios.delete(`${API}/announcements/${id}`);
      toast.success('Dipadam');
      fetchItems();
    } catch (e) {
      toast.error('Gagal padam');
    }
  };

  return (
    <div className="space-y-5" data-testid="announcements-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-red-700" /> Pengumuman
          </h1>
          <p className="text-gray-500 text-xs sm:text-sm">Maklumat penting untuk seluruh team Underads.</p>
        </div>
        {isAdmin && (
          <button onClick={() => setShowForm(true)} data-testid="new-ann-btn"
            className="flex items-center gap-2 px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 text-sm font-medium">
            <Plus className="w-4 h-4" /> Pengumuman Baru
          </button>
        )}
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl p-12 flex justify-center"><Loader className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center text-gray-400">
          <Megaphone className="w-10 h-10 mx-auto mb-2" />
          <p className="text-sm">Tiada pengumuman aktif sekarang.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((a) => {
            const meta = CATEGORY_META[a.category] || CATEGORY_META.general;
            const Icon = meta.icon;
            return (
              <div key={a.id} data-testid={`ann-${a.id}`}
                className={`bg-white rounded-2xl border p-4 sm:p-5 ${a.pinned ? 'border-red-300 ring-1 ring-red-100' : 'border-gray-200'}`}>
                <div className="flex items-start gap-3">
                  <div className={`shrink-0 w-10 h-10 rounded-xl ${meta.color} border flex items-center justify-center`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {a.pinned && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-red-700"><Pin className="w-3 h-3" /> Pinned</span>}
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${meta.color} border`}>
                        {meta.label}
                      </span>
                    </div>
                    <h3 className="text-base font-bold text-gray-800">{a.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed mt-1 whitespace-pre-wrap">{a.body}</p>
                    <p className="text-[11px] text-gray-400 mt-2">
                      Oleh <span className="font-medium">{a.created_by_name || a.created_by}</span> • {new Date(a.created_at).toLocaleString('ms-MY')}
                      {a.expires_at && <> • Tamat: {a.expires_at}</>}
                    </p>
                  </div>
                  {isAdmin && (
                    <button onClick={() => handleDelete(a.id)} title="Padam" data-testid={`ann-delete-${a.id}`}
                      className="p-2 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && <AnnouncementForm onClose={() => setShowForm(false)} onCreated={fetchItems} />}
    </div>
  );
};

export default AnnouncementsPage;
